import { Repository } from "./adapters/repository";

import * as retrieveProjectBudgetSynthesis from "./useCases/retrieveProjectBudgetSynthesis";
import * as createCostProfile from "./useCases/createCostProfile";
import * as retrieveCostProfiles from "./useCases/retrieveCostProfiles";
import * as editCostProfile from "./useCases/editCostProfile";
import * as removeCostProfile from "./useCases/removeCostProfile";
import * as assignCostProfileToResource from "./useCases/assignCostProfileToResource";
import * as removeCostProfileFromResource from "./useCases/removeCostProfileFromResource";
import * as retrieveCostProfileCostsHistory from "./useCases/retrieveCostProfileCostsHistory";
import * as removeCost from "./useCases/removeCost";
import * as editCost from "./useCases/editCost";
import * as editProjectAllocatedBudget from "./useCases/editProjectAllocatedBudget";
import * as retrieveProjectExpenses from "./useCases/retrieveProjectExpenses";
import * as createProjectExpense from "./useCases/createProjectExpense";
import * as retrieveProjectExpenseTypes from "./useCases/retrieveProjectExpenseTypes";
import * as createProjectExpenseType from "./useCases/createProjectExpenseType";
import * as removeProjectExpense from "./useCases/removeProjectExpense";
import * as editProjectExpense from "./useCases/editProjectExpense";
import * as editProjectAllocatedResourcesBudget from "./useCases/editProjectAllocatedResourcesBudget";
import * as editProjectAllocatedExpensesBudget from "./useCases/editProjectAllocatedExpensesBudget";

enum UseCases {
  retrieveProjectBudgetSynthesis = "retrieveProjectBudgetSynthesis",
  createCostProfile = "createCostProfile",
  retrieveCostProfiles = "retrieveCostProfiles",
  editCostProfile = "editCostProfile",
  removeCostProfile = "removeCostProfile",
  assignCostProfileToResource = "assignCostProfileToResource",
  removeCostProfileFromResource = "removeCostProfileFromResource",
  retrieveCostProfileCostsHistory = "retrieveCostProfileCostsHistory",
  removeCost = "removeCost",
  editCost = "editCost",
  editProjectAllocatedBudget = "editProjectAllocatedBudget",
  retrieveProjectExpenses = "retrieveProjectExpenses",
  createProjectExpense = "createProjectExpense",
  retrieveProjectExpenseTypes = "retrieveProjectExpenseTypes",
  createProjectExpenseType = "createProjectExpenseType",
  removeProjectExpense = "removeProjectExpense",
  editProjectExpense = "editProjectExpense",
  editProjectAllocatedResourcesBudget = "editProjectAllocatedResourcesBudget",
  editProjectAllocatedExpensesBudget = "editProjectAllocatedExpensesBudget",
}

interface Core {
  [UseCases.retrieveProjectBudgetSynthesis]: retrieveProjectBudgetSynthesis.Interactor;
  [UseCases.createCostProfile]: createCostProfile.Interactor;
  [UseCases.retrieveCostProfiles]: retrieveCostProfiles.Interactor;
  [UseCases.editCostProfile]: editCostProfile.Interactor;
  [UseCases.removeCostProfile]: removeCostProfile.Interactor;
  [UseCases.assignCostProfileToResource]: assignCostProfileToResource.Interactor;
  [UseCases.removeCostProfileFromResource]: removeCostProfileFromResource.Interactor;
  [UseCases.retrieveCostProfileCostsHistory]: retrieveCostProfileCostsHistory.Interactor;
  [UseCases.removeCost]: removeCost.Interactor;
  [UseCases.editCost]: editCost.Interactor;
  [UseCases.editProjectAllocatedBudget]: editProjectAllocatedBudget.Interactor;
  [UseCases.retrieveProjectExpenses]: retrieveProjectExpenses.Interactor;
  [UseCases.createProjectExpense]: createProjectExpense.Interactor;
  [UseCases.retrieveProjectExpenseTypes]: retrieveProjectExpenseTypes.Interactor;
  [UseCases.createProjectExpenseType]: createProjectExpenseType.Interactor;
  [UseCases.removeProjectExpense]: removeProjectExpense.Interactor;
  [UseCases.editProjectExpense]: editProjectExpense.Interactor;
  [UseCases.editProjectAllocatedResourcesBudget]: editProjectAllocatedResourcesBudget.Interactor;
  [UseCases.editProjectAllocatedExpensesBudget]: editProjectAllocatedExpensesBudget.Interactor;
}

type Boundaries = BoundaryMap<Core>;

type Dependencies = {
  repository: Repository;
};

function createCore(dependencies: Dependencies): Core {
  return {
    [UseCases.retrieveProjectBudgetSynthesis]:
      retrieveProjectBudgetSynthesis.createInteractor(dependencies.repository),
    [UseCases.createCostProfile]: createCostProfile.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveCostProfiles]: retrieveCostProfiles.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editCostProfile]: editCostProfile.createInteractor(
      dependencies.repository,
    ),
    [UseCases.removeCostProfile]: removeCostProfile.createInteractor(
      dependencies.repository,
    ),
    [UseCases.assignCostProfileToResource]:
      assignCostProfileToResource.createInteractor(dependencies.repository),
    [UseCases.removeCostProfileFromResource]:
      removeCostProfileFromResource.createInteractor(dependencies.repository),
    [UseCases.retrieveCostProfileCostsHistory]:
      retrieveCostProfileCostsHistory.createInteractor(dependencies.repository),
    [UseCases.removeCost]: removeCost.createInteractor(dependencies.repository),
    [UseCases.editCost]: editCost.createInteractor(dependencies.repository),
    [UseCases.editProjectAllocatedBudget]:
      editProjectAllocatedBudget.createInteractor(dependencies.repository),
    [UseCases.retrieveProjectExpenses]:
      retrieveProjectExpenses.createInteractor(dependencies.repository),
    [UseCases.createProjectExpense]: createProjectExpense.createInteractor(
      dependencies.repository,
    ),
    [UseCases.retrieveProjectExpenseTypes]:
      retrieveProjectExpenseTypes.createInteractor(dependencies.repository),
    [UseCases.createProjectExpenseType]:
      createProjectExpenseType.createInteractor(dependencies.repository),
    [UseCases.removeProjectExpense]: removeProjectExpense.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editProjectExpense]: editProjectExpense.createInteractor(
      dependencies.repository,
    ),
    [UseCases.editProjectAllocatedResourcesBudget]:
      editProjectAllocatedResourcesBudget.createInteractor(
        dependencies.repository,
      ),
    [UseCases.editProjectAllocatedExpensesBudget]:
      editProjectAllocatedExpensesBudget.createInteractor(
        dependencies.repository,
      ),
  };
}

export type { Core, Boundaries, Dependencies };

export { UseCases, createCore };
